import { useState, useEffect, useMemo } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { MdSave, MdDelete, MdCheck } from 'react-icons/md';
import { parseISO } from 'date-fns';
import { Button } from '@Components/Button/Button';
import { InteractiveSpan } from '@Components/InteractiveEl/InteractiveEl';
import { Appearance, Design, Size } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { AnomalyDetectionLambdaFetch, getUserFacingMessage } from '@src/utils/fetch-utils';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector } from '@src/redux/company';
import { emailSelector } from '@src/redux/user';
import './EditAlertModal.scss';
import { AlertFormData, DEFAULT_ALERT_FORM_DATA } from './anomalyDetectionConstants';
import {
  useBudgetOptions,
  useEmailAddresses,
  useSlackMembers,
  useSlackChannels,
  useUsedBudgetIds,
  useAlertPresets
} from './hooks/useAlertData';
import {
  convertFormDataToDatabaseAlerts,
  hasValidNonEmptyStrings,
  validateMonitoringType,
  validateNotificationMethods
} from './utils/alertUtils';
import { AlertForm } from './components/AlertForm';

interface CreateAlertModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: () => void;
  onPresetSaved?: () => void;
}

export const CreateAlertModal = ({
  show,
  onHide,
  onSubmit,
  onPresetSaved
}: CreateAlertModalProps) => {
  const company = useAppSelector(currentCompanySelector);
  const email = useAppSelector(emailSelector);
  const {
    data: alertPresets = [],
    isLoading: isLoadingPresets,
    error: presetsError,
    refetch: refetchAlertPresets
  } = useAlertPresets(company);
  const defaultFormData: AlertFormData = useMemo(
    () => ({
      ...DEFAULT_ALERT_FORM_DATA,
      ...(alertPresets[0]?.preset_data || {}),
      alertType: 'budget'
    }),
    [alertPresets]
  );

  const {
    data: budgetOptions = [],
    isLoading: isLoadingBudgets,
    error: budgetError,
    refetch: refetchBudgets
  } = useBudgetOptions(company);

  const {
    data: emailAddressOptions = [],
    isLoading: isLoadingEmailAddresses,
    error: emailAddressError,
    refetch: refetchEmails
  } = useEmailAddresses();

  const {
    data: slackMemberOptions = [],
    isLoading: isLoadingSlackMembers,
    error: slackMembersError,
    refetch: refetchSlackMembers
  } = useSlackMembers();

  const {
    data: slackChannelOptions = [],
    isLoading: isLoadingSlackChannels,
    error: slackChannelsError,
    refetch: refetchSlackChannels
  } = useSlackChannels();

  const { data: usedBudgetIds = new Set<number>(), refetch: refetchUsedBudgetIds } =
    useUsedBudgetIds(company);

  const [formData, setFormData] = useState<AlertFormData>(defaultFormData);
  const setError = useSetError();
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [showSavePresetPopup, setShowSavePresetPopup] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [presetSaved, setPresetSaved] = useState(false);
  const [isSqlValid, setIsSqlValid] = useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const presetOptions = alertPresets.map(preset => ({
    value: preset.id,
    label:
      preset.preset_name && preset.description
        ? `${preset.preset_name} | ${preset.description}`
        : preset.preset_name || '',
    presetData: preset.preset_data
  }));

  useEffect(() => {
    if (show) {
      const initialFormData = {
        ...defaultFormData,
        alertType: 'budget' as const
      };
      setFormData(initialFormData);
      setShowSuccessPopup(false);
      setSelectedPresetId(null);
      setPresetSaved(false);
      setIsSqlValid(false);
      setHasAttemptedSave(false);

      refetchAlertPresets();
      refetchBudgets();
      refetchEmails();
      refetchSlackMembers();
      refetchSlackChannels();
      refetchUsedBudgetIds();
    }
  }, [
    show,
    defaultFormData,
    refetchAlertPresets,
    refetchBudgets,
    refetchEmails,
    refetchSlackMembers,
    refetchSlackChannels,
    refetchUsedBudgetIds
  ]);

  useEffect(() => {
    if (show && alertPresets[0]?.preset_data) {
      const presetFormData = {
        ...alertPresets[0].preset_data,
        alertType: 'budget' as const
      };
      setFormData(presetFormData);
      setSelectedPresetId(alertPresets[0].id);
    }
  }, [show, alertPresets]);

  useEffect(() => {
    if (formData.budgetId && budgetOptions.length > 0) {
      const selectedBudget = budgetOptions.find(
        opt => parseInt(opt.value, 10) === formData.budgetId
      );
      const budgetData = selectedBudget?.rowData;

      if (budgetData?.start_date && budgetData?.end_date) {
        const startDate = parseISO(budgetData.start_date).toISOString().split('T')[0];
        const endDate = parseISO(budgetData.end_date).toISOString().split('T')[0];

        setFormData(prev => ({
          ...prev,
          startDate,
          endDate
        }));
      }
    }
  }, [formData.budgetId, budgetOptions]);

  const isFormValid = (): boolean => {
    if (!formData.alertName.trim()) {
      return false;
    }

    if (formData.alertType === 'budget') {
      if (!formData.budgetId || formData.budgetId <= 0) {
        return false;
      }

      if (validateMonitoringType(formData) !== null) {
        return false;
      }

      if (formData.actual.enabled) {
        const actualUnder = formData.actual.under.replace(/[^0-9]/g, '');
        const actualOver = formData.actual.over.replace(/[^0-9]/g, '');
        if (!actualUnder || !actualOver) {
          return false;
        }
      }

      if (formData.projected.enabled) {
        const projectedUnder = formData.projected.under.replace(/[^0-9]/g, '');
        const projectedOver = formData.projected.over.replace(/[^0-9]/g, '');
        if (!projectedUnder || !projectedOver) {
          return false;
        }
      }

      if (formData.remainder.enabled) {
        const remainderUnder = formData.remainder.under.replace(/[^0-9]/g, '');
        const remainderOver = formData.remainder.over.replace(/[^0-9]/g, '');
        if (!remainderUnder || !remainderOver) {
          return false;
        }
      }
    } else if (formData.alertType === 'custom') {
      if (!formData.customSql || !formData.customSql.trim()) {
        return false;
      }

      if (!isSqlValid) {
        return false;
      }

      if (!formData.startDate) {
        return false;
      }
    }

    if (!formData.email.enabled && !formData.slack.enabled) {
      return false;
    }

    if (formData.slack.enabled) {
      if (
        !hasValidNonEmptyStrings(formData.slack.channels) ||
        !hasValidNonEmptyStrings(formData.slack.members)
      ) {
        return false;
      }
    }

    if (formData.email.enabled) {
      if (!hasValidNonEmptyStrings(formData.email.addresses)) {
        return false;
      }
    }

    return true;
  };

  const getFieldValidationErrors = (): Record<string, string> => {
    const fieldErrors: Record<string, string> = {};

    if (!formData.alertName.trim()) {
      fieldErrors.alertName = 'Alert Name is required';
    }

    if (formData.alertType === 'budget') {
      if (!formData.budgetId || formData.budgetId <= 0) {
        fieldErrors.budgetId = 'Budget selection is required';
      }

      const monitoringTypeError = validateMonitoringType(formData);
      if (monitoringTypeError) {
        fieldErrors.monitoringType = monitoringTypeError;
      }

      if (formData.actual.enabled) {
        const actualUnder = formData.actual.under.replace(/[^0-9]/g, '');
        const actualOver = formData.actual.over.replace(/[^0-9]/g, '');
        if (!actualUnder) {
          fieldErrors.actualUnder = "Actual 'Under' threshold is required";
        }
        if (!actualOver) {
          fieldErrors.actualOver = "Actual 'Over' threshold is required";
        }
      }

      if (formData.projected.enabled) {
        const projectedUnder = formData.projected.under.replace(/[^0-9]/g, '');
        const projectedOver = formData.projected.over.replace(/[^0-9]/g, '');
        if (!projectedUnder) {
          fieldErrors.projectedUnder = "Projected 'Under' threshold is required";
        }
        if (!projectedOver) {
          fieldErrors.projectedOver = "Projected 'Over' threshold is required";
        }
      }

      if (formData.remainder.enabled) {
        const remainderUnder = formData.remainder.under.replace(/[^0-9]/g, '');
        const remainderOver = formData.remainder.over.replace(/[^0-9]/g, '');
        if (!remainderUnder) {
          fieldErrors.remainderUnder = "Remainder 'Under' threshold is required";
        }
        if (!remainderOver) {
          fieldErrors.remainderOver = "Remainder 'Over' threshold is required";
        }
      }
    } else if (formData.alertType === 'custom') {
      if (!formData.customSql || !formData.customSql.trim()) {
        fieldErrors.customSql = 'Custom SQL query is required';
      }

      if (!isSqlValid && formData.customSql && formData.customSql.trim()) {
        fieldErrors.customSql = 'Custom SQL must pass validation';
      }

      if (!formData.startDate) {
        fieldErrors.startDate = 'Start date is required';
      }
    }

    const notificationErrors = validateNotificationMethods(formData);
    Object.assign(fieldErrors, notificationErrors);

    return fieldErrors;
  };

  const handleSave = async () => {
    setHasAttemptedSave(true);
    if (isFormValid()) {
      try {
        setIsSaving(true);

        const alertsForDatabase = convertFormDataToDatabaseAlerts(
          formData,
          company,
          budgetOptions,
          email
        );

        await AnomalyDetectionLambdaFetch('/alerts', {
          method: 'POST',
          body: {
            alerts: alertsForDatabase
          }
        });

        setShowSuccessPopup(true);
        onSubmit();
      } catch (error) {
        setError({
          title: "Couldn't create alert",
          message: getUserFacingMessage(error, "Couldn't create alert"),
          reportError: error
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleCancel = () => {
    onHide();
  };

  const handleSaveAsPreset = () => {
    setHasAttemptedSave(true);
    if (isFormValid()) {
      setShowSavePresetPopup(true);
    }
  };

  const handleSavePresetCancel = () => {
    setShowSavePresetPopup(false);
    setPresetName('');
    setPresetDescription('');
  };

  const handleSavePresetSave = async () => {
    const modifiedFormData = {
      ...formData,
      alertName: '',
      endDate: '',
      startDate: '',
      budgetId: 0
    };

    const presetData = {
      company,
      alert_type: 'Budget',
      preset_name: presetName,
      description: presetDescription,
      preset_data: modifiedFormData,
      created_by_email: email
    };

    try {
      setIsSaving(false);

      await AnomalyDetectionLambdaFetch('/company-alert-presets', {
        method: 'POST',
        body: presetData
      });

      setPresetSaved(true);
      setShowSavePresetPopup(false);
      setPresetName('');
      setPresetDescription('');
      onPresetSaved?.();
    } catch (error) {
      setError({
        title: "Couldn't create preset",
        message: getUserFacingMessage(error, "Couldn't create preset"),
        reportError: error
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessPopup(false);
    onHide();
    onSubmit();
  };

  const handleFormDataChange = (newFormData: AlertFormData) => {
    let nextFormData = newFormData;
    if (newFormData.alertType === 'custom' && formData.alertType !== 'custom') {
      const today = new Date();
      const farFuture = new Date();
      farFuture.setFullYear(farFuture.getFullYear() + 10);

      nextFormData = {
        ...newFormData,
        startDate: today.toISOString().split('T')[0],
        endDate: farFuture.toISOString().split('T')[0]
      };
    }

    if (formData.alertType === 'custom' && newFormData.alertType !== 'custom') {
      setIsSqlValid(false);
    }

    setFormData(nextFormData);
  };

  const handlePresetChange = (presetId: string | null) => {
    setSelectedPresetId(presetId);
  };

  const handleSqlValidationChange = (isValid: boolean) => {
    setIsSqlValid(isValid);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size='xl' centered className='edit-alert-modal'>
        <Modal.Header>
          <Modal.Title>My New Alert</Modal.Title>
          <div className='required-fields-note'>*Required fields marked with an asterisk.</div>
        </Modal.Header>

        <Modal.Body>
          <AlertForm
            formData={formData}
            onFormDataChange={handleFormDataChange}
            budgetOptions={budgetOptions}
            emailAddressOptions={emailAddressOptions}
            slackMemberOptions={slackMemberOptions}
            slackChannelOptions={slackChannelOptions}
            usedBudgetIds={usedBudgetIds}
            presetOptions={presetOptions}
            selectedPresetId={selectedPresetId}
            onPresetChange={handlePresetChange}
            isLoadingBudgets={isLoadingBudgets}
            isLoadingEmailAddresses={isLoadingEmailAddresses}
            isLoadingSlackMembers={isLoadingSlackMembers}
            isLoadingSlackChannels={isLoadingSlackChannels}
            isLoadingPresets={isLoadingPresets}
            budgetError={budgetError}
            emailAddressError={emailAddressError}
            slackMembersError={slackMembersError}
            slackChannelsError={slackChannelsError}
            presetsError={presetsError}
            onSqlValidationChange={handleSqlValidationChange}
            initialSqlValid={false}
            fieldErrors={hasAttemptedSave ? getFieldValidationErrors() : {}}
          />
        </Modal.Body>

        <Modal.Footer className='modal-controls'>
          <div className='modal-footer-left'>
            <InteractiveSpan className='delete-icon' onClick={handleCancel} aria-label='Delete'>
              <MdDelete size={25} />
            </InteractiveSpan>
            {formData.alertType !== 'custom' && (
              <Button
                appearance={Appearance.EMPTY}
                onClick={handleSaveAsPreset}
                disabled={presetSaved || (hasAttemptedSave && !isFormValid())}
              >
                {presetSaved ? (
                  <>
                    <MdCheck className='check-icon' />
                    Preset Saved
                  </>
                ) : (
                  'Save as Preset'
                )}
              </Button>
            )}
          </div>
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={handleCancel}>
            Cancel
          </Button>

          <div className='modal-footer-right'>
            <Button
              onClick={handleSave}
              leadingElement={<MdSave />}
              disabled={isSaving || (hasAttemptedSave && !isFormValid())}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            {hasAttemptedSave && !isFormValid() && (
              <div className='validation-error-message'>Please fill out all required fields.</div>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSuccessPopup}
        onHide={handleSuccessClose}
        size='sm'
        centered
        className='success-popup-modal'
      >
        <Modal.Body className='text-center p-4'>
          <div className='success-icon mb-3'>
            <MdSave className='success-icon-svg' />
          </div>
          <h5 className='mb-3'>Alert Created Successfully!</h5>
          <p className='text-muted mb-4'>
            Your new budget alert &quot;{formData.alertName}&quot; has been created and is now
            active.
          </p>
          <Button onClick={handleSuccessClose} size={Size.SM}>
            Continue
          </Button>
        </Modal.Body>
      </Modal>

      <Modal
        show={showSavePresetPopup}
        onHide={handleSavePresetCancel}
        size='sm'
        centered
        className='save-preset-popup-modal'
      >
        <Modal.Header>
          <Modal.Title>Save as Preset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className='mb-3' controlId='presetName'>
            <Form.Label>Preset Name *</Form.Label>
            <Form.Control
              type='text'
              value={presetName}
              onChange={e => setPresetName(e.target.value)}
              placeholder='Enter preset name'
            />
          </Form.Group>
          <Form.Group className='mb-3' controlId='presetDescription'>
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as='textarea'
              rows={3}
              value={presetDescription}
              onChange={e => setPresetDescription(e.target.value)}
              placeholder='Enter description'
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            onClick={handleSavePresetCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePresetSave}
            disabled={!presetName.trim() || !presetDescription.trim()}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
